const MONTH_BY_NUMBER = {
  1: 'styczen',
  2: 'luty',
  3: 'marzec',
  4: 'kwiecien',
  5: 'maj',
  6: 'czerwiec',
  7: 'lipiec',
  8: 'sierpien',
  9: 'wrzesien',
  10: 'pazdziernik',
  11: 'listopad',
  12: 'grudzien',
};

import {
  INTENT,
  getDestinationMode,
  getListingMode,
  getSearchCatalogUrls,
  isListingSearchAction,
  LISTING_MODE,
  resolveFormAction,
} from './offerSearchRules.js';

const getSearchUrls = () => {
  const fromCatalog = getSearchCatalogUrls();
  return {
    children: window.hskiSearch?.schoolChildrenUrl || fromCatalog.children,
    adults: window.hskiSearch?.schoolAdultsUrl || fromCatalog.adults,
    travel: window.hskiSearch?.travelUrl || fromCatalog.travel,
    oferty: window.hskiSearch?.ofertyUrl || fromCatalog.oferty,
    lessonProduct: window.hskiSearch?.lessonProductUrl || fromCatalog.lessonProduct,
    lessonSnowboardProduct: window.hskiSearch?.lessonSnowboardProductUrl || fromCatalog.lessonSnowboardProduct,
    pingwin: fromCatalog.pingwin,
    adultsCourses: fromCatalog.adultsCourses,
    contact: fromCatalog.contact,
  };
};

const emptySearchState = () => ({
  intent: '',
  season: 'winter',
  destinations: [],
  types: [],
  dateFrom: '',
  dateTo: '',
  adults: '',
  children: '',
  childrenAges: [],
  childDobs: [],
  month: '',
  meals: false,
  training: false,
  noTraining: false,
  trainingOnly: false,
  packageOnly: false,
  allInclusive: false,
  transport: false,
  transportType: '',
  board: [],
  amenities: [],
  country: '',
  sort: 'price-asc',
});

const BOARD_SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;
const AMENITY_SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;
const MIN_CHILD_AGE = 0;
const MAX_CHILD_AGE = 17;
const DEFAULT_CHILD_AGE = 8;

const clampChildAge = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_CHILD_AGE;
  return Math.min(MAX_CHILD_AGE, Math.max(MIN_CHILD_AGE, Math.round(n)));
};

const ageFromDobString = (value, asOf = new Date()) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  let date = null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  } else {
    const pl = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (pl) {
      date = new Date(Number(pl[3]), Number(pl[2]) - 1, Number(pl[1]));
    }
  }
  if (!date || Number.isNaN(date.getTime())) return null;
  let age = asOf.getFullYear() - date.getFullYear();
  const md = (asOf.getMonth() + 1) * 100 + asOf.getDate();
  const bd = (date.getMonth() + 1) * 100 + date.getDate();
  if (md < bd) age -= 1;
  if (age < 0) return null;
  return clampChildAge(age);
};

/**
 * Repeated query values: `name[]`, `name`, and PHP `http_build_query` `name[0]`.
 * @param {URLSearchParams} params
 * @param {string} name
 * @returns {string[]}
 */
export const readRepeatedQueryValues = (params, name) => {
  const byIndex = [];
  let hasIndexed = false;
  const indexedRe = new RegExp(`^${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\[(\\d+)\\]$`);
  for (const [key, value] of params.entries()) {
    const match = key.match(indexedRe);
    if (match) {
      hasIndexed = true;
      byIndex[Number(match[1])] = value;
    }
  }
  if (hasIndexed) {
    return byIndex.filter((value) => value !== undefined);
  }
  const bracket = params.getAll(`${name}[]`);
  if (bracket.length) return bracket;
  return params.getAll(name);
};

export const readChildrenAgesFromParams = (params) => {
  const fromAges = readRepeatedQueryValues(params, 'children_ages');
  if (fromAges.length) {
    return fromAges.map((value) => clampChildAge(value));
  }
  return readRepeatedQueryValues(params, 'child_dob')
    .map((dob) => ageFromDobString(dob))
    .filter((n) => n !== null);
};

const collectChildAges = (guestsWrap) => {
  const fromDom = [...(guestsWrap?.querySelectorAll('.js-search-child-age') || [])]
    .map((el) => clampChildAge(el.value));
  if (fromDom.length) return fromDom;
  const pending = guestsWrap?._pendingChildAges;
  if (Array.isArray(pending) && pending.length) {
    return pending.map((n) => clampChildAge(n));
  }
  return [];
};

const sanitizeBoardList = (values = []) => (
  [...new Set((Array.isArray(values) ? values : []).map((v) => String(v).toLowerCase()).filter((v) => BOARD_SLUG_RE.test(v)))]
);

const sanitizeAmenityList = (values = []) => (
  [...new Set(
    (Array.isArray(values) ? values : [])
      .map((v) => String(v).toLowerCase())
      .filter((v) => AMENITY_SLUG_RE.test(v)),
  )]
);

/**
 * Parse server SSOT payload from data-search-state (OfferPresenter / SearchContract).
 */
export const readSearchStateFromRoot = (root) => {
  const searchRoot = root?.closest?.('.js-offer-search') || root;
  const raw = searchRoot?.getAttribute?.('data-search-state') || '';
  if (!raw || raw === '{}' || raw === 'null') {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return {
      ...emptySearchState(),
      ...parsed,
      destinations: Array.isArray(parsed.destinations) ? parsed.destinations : [],
      types: Array.isArray(parsed.types) ? parsed.types : [],
      childrenAges: Array.isArray(parsed.childrenAges)
        ? parsed.childrenAges.map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : [],
      childDobs: Array.isArray(parsed.childDobs) ? parsed.childDobs : [],
      meals: Boolean(parsed.meals),
      training: Boolean(parsed.training),
      noTraining: Boolean(parsed.noTraining),
      trainingOnly: Boolean(parsed.trainingOnly),
      packageOnly: Boolean(parsed.packageOnly),
      allInclusive: Boolean(parsed.allInclusive),
      transport: Boolean(parsed.transport),
      transportType: parsed.transportType || '',
      board: sanitizeBoardList(parsed.board),
      amenities: sanitizeAmenityList(parsed.amenities),
    };
  } catch {
    return null;
  }
};

export const readSearchParams = (search = window.location.search) => {
  const params = new URLSearchParams(search);

  return {
    intent: params.get('intent') || '',
    season: params.get('season') || 'winter',
    destinations: readRepeatedQueryValues(params, 'destination'),
    types: readRepeatedQueryValues(params, 'type'),
    dateFrom: params.get('date_from') || '',
    dateTo: params.get('date_to') || '',
    adults: params.get('adults') || params.get('guests') || '',
    children: params.get('children') || '',
    childrenAges: readChildrenAgesFromParams(params),
    childDobs: readRepeatedQueryValues(params, 'child_dob'),
    month: params.get('month') || '',
    meals: params.has('meals'),
    training: params.has('training') && params.get('training') !== '0',
    noTraining: params.has('no_training'),
    trainingOnly: params.has('training_only'),
    packageOnly: params.has('package_only'),
    allInclusive: params.has('all_inclusive'),
    transport: (() => {
      const raw = params.get('transport') || '';
      if (['own', 'bus', 'plane'].includes(raw)) return raw !== 'own';
      return raw === '1' || raw === 'yes' || raw === 'true';
    })(),
    transportType: (() => {
      const fromType = params.get('transport_type') || '';
      const fromTransport = params.get('transport') || '';
      if (['own', 'bus', 'plane'].includes(fromType)) return fromType;
      if (['own', 'bus', 'plane'].includes(fromTransport)) return fromTransport;
      return '';
    })(),
    board: sanitizeBoardList(readRepeatedQueryValues(params, 'board')),
    amenities: sanitizeAmenityList(readRepeatedQueryValues(params, 'amenity')),
    country: params.get('country') || '',
    sort: params.get('sort') || 'price-asc',
    paged: Math.max(1, Number(params.get('paged') || 1) || 1),
    perPage: Math.max(1, Number(params.get('per_page') || 12) || 12),
  };
};

/**
 * Prefer server data-search-state (normalized contract); fall back to URL.
 * Occupancy (adults/children/ages) from URL fills gaps when server state omitted them.
 */
export const resolveInitialSearchParams = (root) => {
  const fromUrl = readSearchParams();
  const fromServer = readSearchStateFromRoot(root);
  if (fromServer) {
    if ((!fromServer.childrenAges || fromServer.childrenAges.length === 0) && fromServer.childDobs?.length) {
      fromServer.childrenAges = fromServer.childDobs
        .map((dob) => ageFromDobString(dob))
        .filter((n) => n !== null);
    }
    const serverChildren = fromServer.children === '' || fromServer.children == null
      ? 0
      : Number(fromServer.children);
    const urlChildren = fromUrl.children === '' || fromUrl.children == null
      ? 0
      : Number(fromUrl.children);
    if ((!Number.isFinite(serverChildren) || serverChildren <= 0) && urlChildren > 0) {
      fromServer.children = String(urlChildren);
      if (fromUrl.childrenAges?.length) {
        fromServer.childrenAges = [...fromUrl.childrenAges];
      }
      if (fromUrl.childDobs?.length) {
        fromServer.childDobs = [...fromUrl.childDobs];
      }
    } else if (
      (!fromServer.childrenAges || fromServer.childrenAges.length === 0)
      && fromUrl.childrenAges?.length
    ) {
      fromServer.childrenAges = [...fromUrl.childrenAges];
    }
    if ((!fromServer.adults || fromServer.adults === '') && fromUrl.adults) {
      fromServer.adults = fromUrl.adults;
    }
    return fromServer;
  }
  return fromUrl;
};

export const collectFromSearchRoot = (root) => {
  const seasonBtn = document.querySelector('.js-season-tabs .offer-search__season-btn.is-active')
    || root.closest('.js-offer-search')?.querySelector('.offer-search__season-btn.is-active');
  const destinationWrap = root.querySelector('[data-dropdown="destination"]');
  const typeWrap = root.querySelector('[data-dropdown="type"]');
  const dateWrap = root.querySelector('[data-dropdown="date"]');
  const guestsWrap = root.querySelector('[data-dropdown="guests"]');
  const searchRoot = root.closest('.js-offer-search') || root;
  const serverState = readSearchStateFromRoot(searchRoot) || emptySearchState();

  let destinations = [];
  const parents = [...(destinationWrap?.querySelectorAll('.js-dest-parent') || [])];
  const fullCountries = parents
    .filter((item) => item.checked && !item.indeterminate)
    .map((item) => item.value);
  const childDestinations = [...(destinationWrap?.querySelectorAll('.js-dest-child:checked') || [])]
    .filter((el) => {
      const parent = parents.find((item) => item.value === el.dataset.parent);
      // Parent w pełni zaznaczony już reprezentuje wszystkie dzieci.
      return !(parent?.checked && !parent.indeterminate);
    })
    .map((el) => el.value);
  destinations = [...fullCountries, ...childDestinations];

  const rawTypes = [...(typeWrap?.querySelectorAll('.js-type-option:checked') || [])]
    .map((el) => el.value);

  const decision = resolveFormAction(destinations, rawTypes, getSearchUrls());
  const isTravel = decision.intent === INTENT.PACKAGE || decision.intent === INTENT.TRAINING;

  const params = {
    intent: decision.intent,
    season: seasonBtn?.dataset.season || 'winter',
    destinations: decision.destinations,
    types: decision.types,
    dateFrom: '',
    dateTo: '',
    adults: guestsWrap?._guestsState?.adults != null && guestsWrap._guestsState.adults !== ''
      ? String(guestsWrap._guestsState.adults)
      : '',
    // Use != null — children=0 is valid; truthy check would drop it incorrectly
    // if a caller ever relied on an explicit zero (ages still carry count).
    children: guestsWrap?._guestsState?.children != null && guestsWrap._guestsState.children !== ''
      ? String(guestsWrap._guestsState.children)
      : '',
    childrenAges: collectChildAges(guestsWrap),
    meals: false,
    training: false,
    noTraining: false,
    trainingOnly: decision.flags.trainingOnly,
    packageOnly: decision.flags.packageOnly,
    allInclusive: false,
    transport: false,
    // Persist transportType/sort from SSOT when form has no dedicated controls.
    // Country is derived from destination checkboxes (not serverState).
    transportType: serverState.transportType || '',
    board: [],
    amenities: [],
    month: '',
    // Country only when a country parent is fully selected in „Dokąd”.
    // Do not keep stale country from URL/state after the user clears destinations.
    country: fullCountries.length === 1 ? String(fullCountries[0] || '') : '',
    sort: serverState.sort || 'price-asc',
  };

  if (isTravel) {
    params.meals = Boolean(
      searchRoot.querySelector('.js-quick-meals')?.checked
      || document.querySelector('.js-quick-meals')?.checked,
    );
    params.allInclusive = Boolean(
      searchRoot.querySelector('.js-quick-all-inclusive')?.checked
      || document.querySelector('.js-quick-all-inclusive')?.checked,
    );
    params.transport = Boolean(
      searchRoot.querySelector('.js-quick-transport')?.checked
      || document.querySelector('.js-quick-transport')?.checked,
    );
    if (params.transport && params.transportType === 'own') {
      params.transportType = '';
    }
    if (!params.transport && params.transportType && params.transportType !== 'own') {
      params.transportType = '';
    }

    const panelBoard = [];
    const panelAmenities = [];
    let panelTrainingYes = false;
    let panelTrainingNo = false;
    searchRoot.querySelectorAll('.js-filter-chip.is-on, .js-filter-chip[aria-pressed="true"]').forEach((chip) => {
      const group = chip.dataset.group;
      const value = chip.dataset.value;
      if (group === 'board' && BOARD_SLUG_RE.test(value)) panelBoard.push(value);
      if (group === 'amenity' && AMENITY_SLUG_RE.test(value)) panelAmenities.push(value);
      if (group === 'training' && value === 'yes') panelTrainingYes = true;
      if (group === 'training' && value === 'no') panelTrainingNo = true;
    });
    params.board = sanitizeBoardList(panelBoard);
    params.amenities = sanitizeAmenityList(panelAmenities);
    params.training = panelTrainingYes || Boolean(
      searchRoot.querySelector('.js-quick-training')?.checked
      || document.querySelector('.js-quick-training')?.checked,
    );
    params.noTraining = panelTrainingNo;
    // Board chips supersede the coarse meals quick-filter.
    if (params.board.length) {
      params.meals = false;
    }
  }

  const dateState = dateWrap?._dateState;
  if (dateState?.rangeStart) {
    const start = dateState.rangeStart;
    const end = dateState.rangeEnd || dateState.rangeStart;
    params.dateFrom = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
    params.dateTo = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    if (isTravel) {
      params.month = MONTH_BY_NUMBER[start.getMonth() + 1] || '';
    }
  }
  // Do not fall back to data-search-state dates — I3 may have cleared the UI.

  return params;
};

/**
 * Wire-format query string per Search Contract §2.2 — no undocumented flags.
 */
export const buildSearchQueryString = (params) => {
  const query = new URLSearchParams();
  const intent = params.intent || '';
  const isTravel = intent === INTENT.PACKAGE || intent === INTENT.TRAINING;
  const isValidIntent = intent && intent !== INTENT.INVALID;

  if (isValidIntent) query.set('intent', intent);

  if (params.season) query.set('season', params.season);

  (params.destinations || []).forEach((value) => query.append('destination[]', value));

  if (intent !== INTENT.PACKAGE) {
    (params.types || []).forEach((value) => query.append('type[]', value));
  }

  if (params.dateFrom) query.set('date_from', params.dateFrom);
  if (params.dateTo) query.set('date_to', params.dateTo);

  if (params.adults) query.set('adults', params.adults);

  const childrenCount = Number(params.children || 0);
  if (childrenCount > 0) {
    query.set('children', String(childrenCount));
    const ages = [...(params.childrenAges || [])];
    while (ages.length < childrenCount) ages.push(DEFAULT_CHILD_AGE);
    ages.slice(0, childrenCount).forEach((value) => {
      query.append('children_ages[]', String(clampChildAge(value)));
    });
  }

  if (isTravel && params.month) query.set('month', params.month);
  if (isTravel && params.country) query.set('country', params.country);
  if (isTravel && params.sort && params.sort !== 'price-asc') query.set('sort', params.sort);

  if (intent === INTENT.PACKAGE || params.packageOnly) query.set('package_only', '1');
  if (intent === INTENT.TRAINING || params.trainingOnly) query.set('training_only', '1');

  if (isTravel && params.meals) query.set('meals', '1');
  if (isTravel && params.training) query.set('training', '1');
  if (isTravel && params.noTraining) query.set('no_training', '1');
  if (isTravel && params.allInclusive) query.set('all_inclusive', '1');
  if (isTravel && params.transportType) {
    query.set('transport_type', params.transportType);
  } else if (isTravel && params.transport) {
    query.set('transport', '1');
  }
  if (isTravel) {
    sanitizeBoardList(params.board).forEach((value) => query.append('board[]', value));
    sanitizeAmenityList(params.amenities).forEach((value) => query.append('amenity[]', value));
  }

  const paged = Math.max(1, Number(params.paged || 1) || 1);
  if (paged > 1) query.set('paged', String(paged));

  const perPage = Math.max(1, Number(params.perPage || params.per_page || 12) || 12);
  if (perPage !== 12) query.set('per_page', String(perPage));

  return query.toString();
};

/**
 * Persist search state on product / booking URLs (I4 whitelist).
 */
export const buildProductUrlWithSearchState = (baseUrl, params = {}) => {
  if (!baseUrl) return baseUrl;

  const [pathAndQuery, hash = ''] = baseUrl.split('#');
  const [path, existingQuery = ''] = pathAndQuery.split('?');
  const merged = new URLSearchParams(existingQuery);
  const next = new URLSearchParams(buildSearchQueryString(params));

  // Replace contract keys; keep unrelated existing params.
  ['intent', 'season', 'date_from', 'date_to', 'adults', 'children', 'month', 'country', 'sort',
    'meals', 'training', 'no_training', 'training_only', 'package_only', 'all_inclusive', 'transport', 'transport_type',
    'paged', 'per_page'].forEach((key) => {
    merged.delete(key);
  });
  const arrayKeyRe = /^(destination|type|child_dob|children_ages|board|amenity)(\[\d*\])?$/;
  [...merged.keys()].forEach((key) => {
    if (arrayKeyRe.test(key)) merged.delete(key);
  });

  next.forEach((value, key) => {
    if (key.endsWith('[]')) {
      merged.append(key, value);
      return;
    }
    merged.set(key, value);
  });

  const qs = merged.toString();
  const withQuery = qs ? `${path}?${qs}` : path;
  return hash ? `${withQuery}#${hash}` : withQuery;
};

const parseIsoDate = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const applySearchParamsToRoot = (root, params = readSearchParams()) => {
  const searchRoot = root.closest('.js-offer-search') || root;

  document.querySelectorAll('.js-season-tabs .offer-search__season-btn').forEach((btn) => {
    const active = btn.dataset.season === params.season;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-selected', String(active));
  });

  const destinationWrap = searchRoot.querySelector('[data-dropdown="destination"]');
  if (destinationWrap) {
    destinationWrap.querySelectorAll('.js-dest-child, .js-dest-parent').forEach((input) => {
      input.checked = false;
      input.indeterminate = false;
    });
    (params.destinations || []).forEach((slug) => {
      const child = destinationWrap.querySelector(`.js-dest-child[value="${slug}"]`);
      if (child) {
        child.checked = true;
        return;
      }
      const parent = destinationWrap.querySelector(`.js-dest-parent[value="${slug}"]`);
      if (parent) {
        parent.checked = true;
        parent.indeterminate = false;
        destinationWrap.querySelectorAll(`.js-dest-child[data-parent="${parent.value}"]`).forEach((child) => {
          child.checked = true;
        });
      }
    });
    destinationWrap.querySelectorAll('.js-dest-parent').forEach((parent) => {
      const parentValue = parent.value;
      const siblings = destinationWrap.querySelectorAll(`.js-dest-child[data-parent="${parentValue}"]`);
      if (!siblings.length) return;
      if ([...siblings].some((item) => item.checked)) {
        parent.checked = [...siblings].every((item) => item.checked);
        parent.indeterminate = !parent.checked && [...siblings].some((item) => item.checked);
      }
    });
  }

  const typeWrap = searchRoot.querySelector('[data-dropdown="type"]');
  if (typeWrap) {
    typeWrap.querySelectorAll('.js-type-option').forEach((input) => {
      input.checked = (params.types || []).includes(input.value);
    });
  }

  const dateWrap = searchRoot.querySelector('[data-dropdown="date"]');
  if (dateWrap) {
    const state = dateWrap._dateState || {
      rangeStart: null,
      rangeEnd: null,
      viewMonth: '2026-01',
      allowSingleDay: false,
    };
    state.rangeStart = parseIsoDate(params.dateFrom);
    state.rangeEnd = parseIsoDate(params.dateTo) || state.rangeStart;
    if (state.rangeStart && params.dateFrom) {
      state.viewMonth = params.dateFrom.slice(0, 7);
    }
    dateWrap._dateState = state;
  }

  const guestsWrap = searchRoot.querySelector('[data-dropdown="guests"]');
  if (guestsWrap) {
    if (!guestsWrap._guestsState) {
      guestsWrap._guestsState = { adults: 2, children: 0 };
    }
    guestsWrap._guestsState.adults = params.adults !== '' && params.adults != null
      ? Number(params.adults)
      : guestsWrap._guestsState.adults;
    guestsWrap._guestsState.children = params.children !== '' && params.children != null
      ? Number(params.children)
      : 0;
    if (!Number.isFinite(guestsWrap._guestsState.adults) || guestsWrap._guestsState.adults < 1) {
      guestsWrap._guestsState.adults = 2;
    }
    if (!Number.isFinite(guestsWrap._guestsState.children) || guestsWrap._guestsState.children < 0) {
      guestsWrap._guestsState.children = 0;
    }
    const adultsEl = guestsWrap.querySelector('.js-guests-adults');
    const childrenEl = guestsWrap.querySelector('.js-guests-children');
    if (adultsEl) {
      adultsEl.textContent = String(guestsWrap._guestsState.adults);
      adultsEl.dataset.value = String(guestsWrap._guestsState.adults);
    }
    if (childrenEl) {
      childrenEl.textContent = String(guestsWrap._guestsState.children);
      childrenEl.dataset.value = String(guestsWrap._guestsState.children);
    }
    guestsWrap._pendingChildAges = (params.childrenAges && params.childrenAges.length)
      ? params.childrenAges.map((n) => clampChildAge(n))
      : (params.childDobs || []).map((dob) => ageFromDobString(dob)).filter((n) => n !== null);
  }

  const quickMeals = searchRoot.querySelector('.js-quick-meals') || document.querySelector('.js-quick-meals');
  const quickAllInclusive = searchRoot.querySelector('.js-quick-all-inclusive')
    || document.querySelector('.js-quick-all-inclusive');
  const quickTransport = searchRoot.querySelector('.js-quick-transport')
    || document.querySelector('.js-quick-transport');
  const quickTraining = searchRoot.querySelector('.js-quick-training')
    || document.querySelector('.js-quick-training');

  if (quickMeals) quickMeals.checked = Boolean(params.meals) && !(params.board || []).length;
  if (quickAllInclusive) quickAllInclusive.checked = Boolean(params.allInclusive);
  if (quickTransport) {
    quickTransport.checked = Boolean(params.transport)
      || (Boolean(params.transportType) && params.transportType !== 'own');
  }
  if (quickTraining) quickTraining.checked = Boolean(params.training);

  const board = sanitizeBoardList(params.board);
  const amenities = sanitizeAmenityList(params.amenities);
  searchRoot.querySelectorAll('.js-filter-chip').forEach((chip) => {
    const group = chip.dataset.group;
    const value = chip.dataset.value;
    let on = false;
    if (group === 'board') on = board.includes(value);
    if (group === 'amenity') on = amenities.includes(value);
    if (group === 'training' && value === 'yes') on = Boolean(params.training);
    if (group === 'training' && value === 'no') on = Boolean(params.noTraining);
    chip.classList.toggle('is-on', on);
    chip.setAttribute('aria-pressed', String(on));
  });

  const badge = searchRoot.querySelector('.js-filters-badge');
  if (badge) {
    const count = board.length + amenities.length
      + (params.training ? 1 : 0)
      + (params.noTraining ? 1 : 0);
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }
};

/**
 * Init hydration from data-search-state before first user interaction.
 */
export const initSearchStateFromServer = (root) => {
  const searchRoot = root?.closest?.('.js-offer-search') || root;
  if (!searchRoot) return null;

  const params = resolveInitialSearchParams(searchRoot);
  applySearchParamsToRoot(searchRoot, params);
  return params;
};

export const navigateWithSearchParams = (action, params) => {
  const destinations = params.destinations || [];
  const types = params.types || [];
  const decision = params.intent
    ? {
        valid: true,
        action,
        intent: params.intent,
        types,
        flags: {
          packageOnly: Boolean(params.packageOnly),
          trainingOnly: Boolean(params.trainingOnly),
        },
      }
    : resolveFormAction(destinations, types, getSearchUrls());

  if (!decision.valid) return;

  const wireParams = {
    ...params,
    intent: decision.intent,
    types: decision.types,
    packageOnly: decision.flags.packageOnly,
    trainingOnly: decision.flags.trainingOnly,
  };

  const query = buildSearchQueryString(wireParams);
  const resolvedAction = action || decision.action;
  if (!resolvedAction) return;

  const url = query ? `${resolvedAction}?${query}` : resolvedAction;
  window.location.assign(url);
};

/**
 * Navigate to listing (/oferty/) or product/landing page based on search decision.
 */
export const navigateFromSearchDecision = (decision, params, baseUrls = getSearchUrls()) => {
  if (!decision?.valid || !decision.action) return;

  if (isListingSearchAction(decision.action, baseUrls)) {
    navigateWithSearchParams(decision.action, {
      ...params,
      intent: decision.intent,
      types: decision.types,
      packageOnly: decision.flags.packageOnly,
      trainingOnly: decision.flags.trainingOnly,
    });
    return;
  }

  const typeSlug = (decision.types || [])[0] || '';
  const mode = getDestinationMode(params.destinations || []);
  const listingMode = getListingMode(typeSlug);
  const cardKind = listingMode === LISTING_MODE.LANDING ? 'landing' : 'product';
  const detailParams = pickProductDetailParams(params, { cardKind, mode, typeSlug });
  const url = buildProductUrlWithSearchState(decision.action, detailParams);
  window.location.assign(url);
};

/**
 * Subset query for product/landing detail URLs (parity z SearchContract::detail_query_params).
 * @param {object} params
 * @param {{ cardKind?: string, mode?: string, typeSlug?: string }} options
 */
export const pickProductDetailParams = (params = emptySearchState(), options = {}) => {
  const cardKind = options.cardKind || 'product';
  const typeSlug = options.typeSlug || '';
  let mode = options.mode || '';

  if (cardKind === 'inventory') {
    return { ...params };
  }

  const destinations = [...(params.destinations || [])].filter(Boolean);
  if (!mode || mode === 'invalid' || mode === 'any') {
    mode = getDestinationMode(destinations);
  }

  const types = typeSlug
    ? [typeSlug]
    : (params.types || []).filter((t) => t && t !== 'wyjazd-hotel');

  const next = emptySearchState();
  next.season = params.season || 'winter';
  next.destinations = destinations;
  next.types = types;

  if (cardKind === 'landing') {
    return next;
  }

  if (mode === 'foreign') {
    next.dateFrom = params.dateFrom || '';
    next.dateTo = params.dateTo || '';
    next.month = params.month || '';
    next.adults = params.adults || '';
    next.children = params.children || '';
    next.childrenAges = [...(params.childrenAges || [])];
  }

  return next;
};
